import { asList } from '../lib/api';
import { useState, useEffect } from 'react';
import { formatDate, formatDateTime } from '../lib/dates';
import { automationApi, type Catalogue, type Rule, type Run, type RuleTemplate, type ActionInfo } from '../lib/automationApi';

/**
 * Automation admin. A school builds rules ("when X, do Y"), adopts ready-made templates in one click,
 * turns rules on/off, and sees what actually fired. Rules ship inactive — automation is opt-in.
 */
export function AutomationPage() {
  const [cat, setCat] = useState<Catalogue | null>(null);
  const [rules, setRules] = useState<Rule[]>([]);
  const [runs, setRuns] = useState<Run[]>([]);
  const [tab, setTab] = useState<'rules' | 'templates' | 'activity'>('rules');
  const [building, setBuilding] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = () => {
    automationApi.rules().then((r) => setRules(asList(r))).catch(() => setRules([]));
    automationApi.runs().then((r) => setRuns(asList(r))).catch(() => setRuns([]));
  };
  useEffect(() => { automationApi.catalogue().then(setCat).catch(() => setCat(null)); load(); }, []);

  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(null), 2500); };

  const toggle = async (r: Rule) => {
    try { await automationApi.setActive(r.id, !r.isActive); load(); flash(r.isActive ? 'Rule turned off.' : 'Rule is now live.'); }
    catch { flash('Could not update.'); }
  };

  const adopt = async (t: RuleTemplate) => {
    try {
      await automationApi.create({ name: t.name, triggerKey: t.triggerKey, conditionsJson: t.conditionsJson, actionsJson: t.actionsJson });
      setTab('rules'); load(); flash('Template added — turn it on when you\'re ready.');
    } catch { flash('Could not add template.'); }
  };

  const triggerLabel = (key: string) => cat?.triggers.find((t) => t.key === key)?.label ?? key;

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Automation</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Rules</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4, maxWidth: 680 }}>
          Let the system act for you — "when a fee is 15 days overdue, tell the class teacher", "when a
          website enquiry comes in, notify admissions", "when an achievement is posted, put it on the
          public page". Rules stay off until you switch them on.
        </p>
      </div>

      {msg && <div className="pill" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', justifyContent: 'flex-start', marginBottom: 14, fontSize: 13 }}>{msg}</div>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {(['rules', 'templates', 'activity'] as const).map((t) => (
          <button key={t} className={`btn ${tab === t ? 'btn-accent' : ''}`} onClick={() => setTab(t)} style={{ textTransform: 'capitalize' }}>{t}</button>
        ))}
        <div style={{ marginLeft: 'auto' }}>
          {tab === 'rules' && <button className="btn btn-accent" onClick={() => setBuilding(true)}>+ New rule</button>}
        </div>
      </div>

      {tab === 'rules' && (
        rules.length === 0
          ? <Empty title="No rules yet" body="Add one from Templates, or build your own with “New rule”." />
          : <div style={{ display: 'grid', gap: 10 }}>
              {rules.map((r) => (
                <div key={r.id} className="card" style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{r.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                      When {triggerLabel(r.triggerKey)}
                      {r.fireCount > 0 && ` · fired ${r.fireCount}×`}
                      {r.lastFiredAt && ` · last ${formatDate(r.lastFiredAt)}`}
                    </div>
                  </div>
                  <span className="pill" style={{ fontSize: 11, color: r.isActive ? 'var(--paid,#2e7d32)' : 'var(--ink-3)' }}>
                    {r.isActive ? '● Live' : '○ Off'}
                  </span>
                  <button className={`btn ${r.isActive ? '' : 'btn-accent'}`} onClick={() => toggle(r)}>
                    {r.isActive ? 'Turn off' : 'Turn on'}
                  </button>
                </div>
              ))}
            </div>
      )}

      {tab === 'templates' && cat && (
        <div style={{ display: 'grid', gap: 10 }}>
          {cat.templates.map((t, i) => (
            <div key={i} className="card" style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{t.name}</div>
                <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{t.description}</div>
              </div>
              <button className="btn" onClick={() => adopt(t)}>Add</button>
            </div>
          ))}
        </div>
      )}

      {tab === 'activity' && (
        runs.length === 0
          ? <Empty title="Nothing has fired yet" body="When a rule runs, it shows here — what happened and whether it worked." />
          : <div style={{ display: 'grid', gap: 8 }}>
              {runs.map((r) => (
                <div key={r.id} className="card" style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 16 }}>{r.success ? '✅' : '⚠️'}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{r.ruleName}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{r.summary}</div>
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{formatDateTime(r.ranAt)}</span>
                </div>
              ))}
            </div>
      )}

      {building && cat && <RuleBuilder cat={cat} onClose={() => setBuilding(false)} onSaved={() => { setBuilding(false); load(); flash('Rule created — turn it on when ready.'); }} />}
    </div>
  );
}

function RuleBuilder({ cat, onClose, onSaved }: { cat: Catalogue; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState('');
  const [triggerKey, setTriggerKey] = useState(cat.triggers[0]?.key ?? '');
  const [condField, setCondField] = useState('');
  const [condOp, setCondOp] = useState('gte');
  const [condVal, setCondVal] = useState('');
  const [actions, setActions] = useState<Array<{ type: string; params: Record<string, string> }>>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trigger = cat.triggers.find((t) => t.key === triggerKey);

  const addAction = (a: ActionInfo) => setActions([...actions, { type: a.type, params: {} }]);
  const setParam = (i: number, k: string, v: string) => setActions(actions.map((a, j) => j === i ? { ...a, params: { ...a.params, [k]: v } } : a));

  const save = async () => {
    if (!name.trim()) { setError('Give the rule a name.'); return; }
    if (actions.length === 0) { setError('Add at least one action.'); return; }
    const conditionsJson = condField && condVal
      ? JSON.stringify({ [condField]: { [condOp]: isNaN(Number(condVal)) ? condVal : Number(condVal) } })
      : '{}';
    const actionsJson = JSON.stringify(actions.map((a) => ({ type: a.type, ...a.params })));
    setBusy(true); setError(null);
    try { await automationApi.create({ name: name.trim(), triggerKey, conditionsJson, actionsJson }); onSaved(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not save.'); }
    finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 100, display: 'grid', placeItems: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ padding: 22, width: '100%', maxWidth: 560, maxHeight: '88vh', overflow: 'auto' }}>
        <h2 className="h-display" style={{ fontSize: 22, marginBottom: 16 }}>New rule</h2>
        {error && <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{error}</div>}

        <label className="label" style={{ fontSize: 12 }}>Name</label>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Overdue → tell teacher" style={{ marginBottom: 14 }} />

        <label className="label" style={{ fontSize: 12 }}>When…</label>
        <select className="input" value={triggerKey} onChange={(e) => { setTriggerKey(e.target.value); setCondField(''); }} style={{ marginBottom: 14 }}>
          {cat.triggers.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>

        <label className="label" style={{ fontSize: 12 }}>Only if (optional)</label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <select className="input" value={condField} onChange={(e) => setCondField(e.target.value)} style={{ flex: 1 }}>
            <option value="">— any —</option>
            {trigger?.fields.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          {condField && <>
            <select className="input" value={condOp} onChange={(e) => setCondOp(e.target.value)} style={{ width: 90 }}>
              <option value="gte">≥</option><option value="lte">≤</option><option value="gt">&gt;</option>
              <option value="lt">&lt;</option><option value="eq">=</option><option value="ne">≠</option>
            </select>
            <input className="input" value={condVal} onChange={(e) => setCondVal(e.target.value)} placeholder="value" style={{ width: 100 }} />
          </>}
        </div>

        <label className="label" style={{ fontSize: 12 }}>Then…</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          {cat.actions.map((a) => <button key={a.type} className="btn" onClick={() => addAction(a)}>+ {a.label}</button>)}
        </div>
        <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
          {actions.map((a, i) => {
            const info = cat.actions.find((x) => x.type === a.type);
            return (
              <div key={i} className="card" style={{ padding: 12, background: 'var(--surface-2,#f6f6f4)' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: info?.params.length ? 8 : 0 }}>
                  <strong style={{ fontSize: 13 }}>{info?.label ?? a.type}</strong>
                  <button className="btn" onClick={() => setActions(actions.filter((_, j) => j !== i))} style={{ marginLeft: 'auto', padding: '2px 8px' }}>✕</button>
                </div>
                {info?.params.map((p) => (
                  <input key={p} className="input" placeholder={p} value={a.params[p] ?? ''} onChange={(e) => setParam(i, p, e.target.value)} style={{ marginBottom: 6, fontSize: 13 }} />
                ))}
                {a.type.startsWith('notify') && <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>Tip: use {'{studentName}'} etc. in title/body to insert event details.</div>}
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-accent" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Create rule'}</button>
        </div>
      </div>
    </div>
  );
}

function Empty({ title, body }: { title: string; body: string }) {
  return (
    <div className="card" style={{ padding: 40, textAlign: 'center' }}>
      <h3 style={{ marginBottom: 6 }}>{title}</h3>
      <p style={{ color: 'var(--ink-3)', fontSize: 14 }}>{body}</p>
    </div>
  );
}
